import { createAsyncThunk } from '@reduxjs/toolkit';
import apiManager from '../../../api/api-manager';
import {
  GetTasksResponse,
  PersonaTipoUtenteEnum,
  QuestionnairesStatus,
  SpecimenUploadRequest,
  SpecimenUploadResponse,
  Status,
} from '../../../api/generated';
import { PraticaSelf } from '../../../api/generated/dob';
import { DashboardWalletSummary, FinancialAdvisor, News } from '../../../model';
import { UnreadDocumentsCount } from '../../../model/documents/unread-documents-count';
import homeRepository from '../../../repositories/home/home-repository';
import {
  getAPIRequestHeaders,
  getAPIRequestHeadersProspect,
  GetThunkAPI,
} from '../../../utils/api-utils';
import { AsyncThunkConfig } from '../../async-thunk-config';
import { UserState } from '../../data';
import { DossierState, getDossierFilter } from '../../data/dossier';
import { getDossierFilterCode } from '../wallet';
import {
  HomeError,
  HomeState,
  HomeWalletSectionMeta,
  UpdateOnboardingCompileTypeRequest,
} from './home-types';

import {
  signatureArea,
} from '../profile/signature-area/signature-area-thunk';

export const downloadHomeData = createAsyncThunk<
  void,
  { customerCode?: string; bundleCode?: string },
  AsyncThunkConfig<HomeError>
>('download-home-data', async (params, thunkApi) => {
  const userState: UserState = thunkApi.getState().data.user;
  thunkApi.dispatch(
    downloadFinancialAdvisorProfile(userState.codiceAgente || '')
  );
  if (!userState.isProspect || params.customerCode) {
    if (!userState.informativeMode) {
      thunkApi.dispatch(signatureArea(params));
    }
    thunkApi.dispatch(retrieveUnreadDocumentsCount());
  }
  thunkApi.dispatch(retrieveLatestNews());
  if (userState.isProspect) {
    thunkApi.dispatch(getTasks());
  }
});

export const logoutThunk = createAsyncThunk(
  'home/logout',
  async (_, thunkApi: any) => {
    try {
      return await homeRepository.logout(thunkApi);
    } catch (error) {
      console.error(error);
      return thunkApi.rejectWithValue('GENERIC_ERROR');
    }
  }
);

export const retrieveUnreadDocumentsCount = createAsyncThunk<
  number,
  void,
  AsyncThunkConfig<HomeError>
>('retrieve-unread-documents-count', async (_, thunkApi) => {
  try {
    const isProspect: UserState = thunkApi.getState().data.user.isProspect;

    if (isProspect) {
      return 0;
    }
    const response: UnreadDocumentsCount =
      await homeRepository.getUnreadDocumentsCount(thunkApi);
    return (
      Object.values(response.counts).reduce((sum, curr) => sum + curr, 0) +
      response.totalCommunications +
      response.totalOffers
    );
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const retrieveDocumentsToSignCount = createAsyncThunk<
  number,
  { customerCode?: string; bundleCode?: string },
  AsyncThunkConfig<HomeError>
>('retrieve-documents-to-sign-count', async (params, thunkApi) => {
  try {
    return await homeRepository.getDocumentsToSignCount(
      thunkApi,
      params?.customerCode,
      params?.bundleCode
    );
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const retrieveLatestNews = createAsyncThunk<
  News,
  void,
  AsyncThunkConfig<HomeError>
>('retrieve-latest-news', async (_, thunkApi) => {
  try {
    return await homeRepository.getLatestNews(thunkApi);
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const downloadFinancialAdvisorProfile = createAsyncThunk<
  FinancialAdvisor,
  string,
  AsyncThunkConfig<HomeError>
>('download-financial-advisor-profile', async (codiceAgente, thunkApi) => {
  try {
    const userState: UserState = thunkApi.getState().data.user;
    if (codiceAgente !== '') {
      return homeRepository.getDatiAgente(thunkApi, codiceAgente);
    }

    if (!userState.isProspect) {
      return homeRepository.getFinancialAdvisorProfile(
        thunkApi,
        userState.userType === PersonaTipoUtenteEnum.Pg
          ? userState.userBehalfOfCode
          : userState.userCode
      );
    }

    return thunkApi.rejectWithValue('GENERIC_ERROR');
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const retrieveHomeWalletSummary = createAsyncThunk<
  DashboardWalletSummary,
  void,
  AsyncThunkConfig<HomeError> & {
    pendingMeta: HomeWalletSectionMeta;
    fulfilledMeta: HomeWalletSectionMeta;
    rejectedMeta: HomeWalletSectionMeta;
  }
>(
  'retrieve-home-wallet-summary',
  async (_, thunkApi) => {
    // The tab can change while the request is running: the result belongs to
    // the tab that started it, not to the one selected when it arrives.
    const homeState: HomeState = thunkApi.getState().features.home;
    const meta: HomeWalletSectionMeta = { section: homeState.selectedSection };
    try {
      const dossierState: DossierState = thunkApi.getState().data.dossier;
      const dossierFilter = getDossierFilter(
        dossierState.dossierFilter,
        homeState.selectedSection
      );
      // The repository only reads state and request headers from thunkApi; its
      // type does not know the fulfilled/rejected meta declared on this thunk.
      const summary = await homeRepository.getWalletSummary(
        thunkApi as unknown as GetThunkAPI<AsyncThunkConfig<HomeError>>,
        getDossierFilterCode(dossierFilter)
      );
      return thunkApi.fulfillWithValue(summary, meta);
    } catch (error) {
      console.error(error);
      return thunkApi.rejectWithValue('GENERIC_ERROR', meta);
    }
  },
  {
    getPendingMeta: (_, { getState }) => ({
      section: (getState().features.home as HomeState).selectedSection,
    }),
  }
);

export const getTasks = createAsyncThunk<
  GetTasksResponse,
  void,
  AsyncThunkConfig<HomeError>
>('get-tasks', async (_, thunkApi) => {
  try {
    const userState = thunkApi.getState().data.user;
    const response = await apiManager.tasksApi.getTasks(
      userState?.username,
      getAPIRequestHeaders(thunkApi, { Accept: 'application/json' })
    );

    return response.data;
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const uploadSpecimen = createAsyncThunk<
  SpecimenUploadResponse,
  SpecimenUploadRequest,
  AsyncThunkConfig<HomeError>
>('upload-specimen', async (request: SpecimenUploadRequest, thunkApi) => {
  try {
    const userState = thunkApi.getState().data.user;
    const response = await apiManager.tasksApi.uploadSpecimen(
      userState?.username,
      request,
      getAPIRequestHeaders(thunkApi, { Accept: 'application/json' })
    );

    return response.data;
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const startSigningProcess = createAsyncThunk<
  Status,
  void,
  AsyncThunkConfig<HomeError>
>('start-signing-process', async (_, thunkApi) => {
  try {
    const userState = thunkApi.getState().data.user;
    const practiceDataState =
      thunkApi.getState().features.home.practiceData?.value;
    const response = await apiManager.signingApi.startSigningProcess(
      userState?.username,
      practiceDataState?.codicePratica || '',
      getAPIRequestHeadersProspect(thunkApi, { Accept: 'application/json' })
    );

    return response.data;
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const retrieveOnboardingSelfPracticeData = createAsyncThunk<
  PraticaSelf,
  void,
  AsyncThunkConfig<HomeError>
>('retrieve-onboarding-self-practice-data', async (_, thunkApi) => {
  try {
    const isProspect: UserState = thunkApi.getState().data.user.isProspect;
    if (isProspect) {
      const response = await apiManager.onboardingServizioApi.getPraticaSelf(
        (thunkApi.getState().data.user as UserState)?.taxCode,
        getAPIRequestHeaders(thunkApi)
      );
      return response.data.pratica;
    } else {
      return undefined;
    }
  } catch (error) {
    console.error(error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

export const updateOnboardingCompileType = createAsyncThunk<
  string,
  UpdateOnboardingCompileTypeRequest,
  AsyncThunkConfig<HomeError>
>(
  'update-onboarding-compile-type',
  async (request: UpdateOnboardingCompileTypeRequest, thunkApi) => {
    try {
      await apiManager.onboardingServizioApi.postUpdateMifidCompileType(
        request.codicePratica,
        { compileType: request.compileType },
        getAPIRequestHeaders(thunkApi)
      );
      return request.compileType;
    } catch (error) {
      console.error(error);
      return thunkApi.rejectWithValue('GENERIC_ERROR');
    }
  }
);

export const updateQuestionnairesStatus = createAsyncThunk<
  void,
  QuestionnairesStatus,
  AsyncThunkConfig<HomeError>
>(
  'update-questionnaires-status',
  async (request: QuestionnairesStatus, thunkApi) => {
    try {
      const userState = thunkApi.getState().data.user;

      await apiManager.updateQuestionnairesStatus.updateQuestionnairesStatus(
        userState.username,
        request,
        getAPIRequestHeaders(thunkApi)
      );
      return null;
    } catch (error) {
      console.error(error);
      return thunkApi.rejectWithValue('GENERIC_ERROR');
    }
  }
);
